// "Control-only" personas — three DELIBERATELY different predicates (CB-140).
//
// All three surfaces started from one definition (commit 767b4ac:
// hasControl = control.devices | broadcast.live, admin disqualifiers =
// manage.fleet | manage.locations | manage.members). They then diverged:
//
//   - fb04e88 widened the SERVER predicate's control set with
//     broadcast.announce + control.remote_desktop (control.live_feedback was
//     also added, then removed in 5a2fd27) — an authorization change: a
//     floor-control member holding only the newer control capabilities must
//     still be treated as scope-restricted (deny-on-empty-scope).
//   - cd65016 reshaped the WEB predicate into a landing/routing heuristic
//     (`isControlPrimary`): only control.devices counts as control, and any
//     of manage.fleet | manage.members | manage.media | manage.schedules or
//     view.dashboard disqualifies — a dashboard-capable user should land on
//     /dashboard, not /control-selection.
//   - the MOBILE rebuild (dac5943) kept the original 767b4ac capability
//     shape, adding a local isWorkspaceCreator guard in the app shim. Web's
//     team UI (permission-group-labels.ts) kept the same original shape for
//     labeling permission groups.
//
// The three predicates answer three different questions — authorization
// floor (server) vs landing surface (web routing) vs companion-app /
// group-labeling gate (mobile + team UI) — so they are consolidated here as
// SEPARATE named exports rather than silently merged. Only
// `isControlOnlyPersona` is authorization-authoritative; the other two are
// UX heuristics and must never be used for server-side authorization.

import type { EffectivePermissions } from "./model.ts";

/**
 * SERVER authorization predicate (the authority). Floor-control persona:
 * holds an operate/broadcast capability but no fleet-admin capability, so an
 * empty scope means "no devices" rather than "workspace-wide" (see
 * DENY_ON_EMPTY_SCOPE and api-server access-scope.ts / member-access.ts).
 */
export function isControlOnlyPersona(perms: EffectivePermissions): boolean {
  if (perms.hasImplicitAll) return false;
  const caps = perms.capabilities;
  const hasControl =
    caps.includes("control.devices") ||
    caps.includes("broadcast.live") ||
    caps.includes("broadcast.announce") ||
    caps.includes("control.remote_desktop");
  const hasFleetAdmin =
    caps.includes("manage.fleet") ||
    caps.includes("manage.locations") ||
    caps.includes("manage.members");
  return hasControl && !hasFleetAdmin;
}

/**
 * WEB landing/routing heuristic: control is this member's PRIMARY surface,
 * so send them to /control-selection instead of an admin page. Deliberately
 * narrower than the server persona: only control.devices counts as control,
 * and dashboard access or any content/fleet admin capability disqualifies.
 * UX only — never use for authorization.
 */
export function isControlPrimaryCapabilities(
  caps: readonly string[],
): boolean {
  const hasControl = caps.includes("control.devices");
  const hasAdmin =
    caps.includes("manage.fleet") ||
    caps.includes("manage.members") ||
    caps.includes("manage.media") ||
    caps.includes("manage.schedules");
  return hasControl && !hasAdmin && !caps.includes("view.dashboard");
}

/**
 * MOBILE companion-app gate + web team-UI group label: the original
 * (pre-fb04e88) control-only shape over a bare capability list. The mobile
 * shim additionally excludes workspace creators; the web team UI uses it to
 * label permission groups as control-only. UX only — never use for
 * authorization (the server predicate's control set is wider).
 */
export function isControlOnlyCapabilities(caps: readonly string[]): boolean {
  const hasControl =
    caps.includes("control.devices") || caps.includes("broadcast.live");
  const hasFleetAdmin =
    caps.includes("manage.fleet") ||
    caps.includes("manage.locations") ||
    caps.includes("manage.members");
  return hasControl && !hasFleetAdmin;
}
